/**
 * ══════════════════════════════════════════════════════════════════════════
 * ANA AKIŞ — TAKİP EDİLEN GİZLİ YAZARLARIN GÖNDERİLERİ
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── ÖLÇÜLEN KUSUR (2026-08-25) ───────────────────────────────────────────
 * Ana akışın (`/`) İKİ kolu vardı ve **aynı kullanıcıya farklı içerik**
 * gösteriyorlardı:
 *
 *   · **materialized kol** (`homeFeedMaterialized.ts`) — `followingFeed`
 *     kayıtlarını per-doc `getDoc` ile hidratlar. Gizlilik süzgeci YOK;
 *     kural katmanı karar verir. → takip ettiğin gizli hesabın gönderisi
 *     **GÖRÜNÜR**.
 *   · **fallback kol** (`Feed.tsx`) — sorguların hepsi
 *     `where('authorIsPrivate','==',false)` taşır. → aynı gönderi
 *     **HİÇ GÖRÜNMEZ**.
 *
 * Hangi kolun koştuğu kullanıcıdan kullanıcıya değişiyor (`rawCount === 0`
 * → sessizce fallback) ve hiçbir yerde belirtilmiyordu.
 *
 * ── YÖN: MATERIALIZED KOL DOĞRU (kural katmanından ÖLÇÜLDÜ) ──────────────
 * Karar `firestore.rules`tan okundu, tercihle değil:
 *
 *   `posts` (satır ~1524/1539) — **get VE list**:
 *       resource.data.authorIsPrivate != true
 *         || canReadPrivateContent(resource.data.authorId)
 *     `canReadPrivateContent` = sahibi **VEYA onaylı takipçi edge'i**
 *     (`users/{yazar}/followers/{ben}` exists). → takipçinin gizli yazarın
 *     gönderisini **listelemesi kural katmanında MEŞRU**.
 *
 * Yani fallback kolun `authorIsPrivate == false` filtresi bir güvenlik
 * kısıtı DEĞİL, bir **maliyet kısıtıdır** — ve `Feed.tsx`in kendi yorumu
 * bunu zaten yazıyordu: *"30 yazarlık in-sorgusunda yazar başına exists()
 * bütçesi aşılıyor (emulator ölçümü) … Faz 3'te materialized following-feed
 * bu kısıtı kaldıracak."*
 *
 * Bu modül o kısıtı fallback kolda da kaldırır, ama **bütçeyi patlatmadan**:
 * toplu `in` sorgusu yerine gizli yazar başına TEK yazarlık sorgu. Kural
 * o şekli `canReadPrivateContent` ile geçirir ve doküman erişimi
 * `GIZLI_YAZAR_POST_LIMITI` ile sınırlıdır.
 *
 * ── ⚠️ KLİPLER KAPSAM DIŞI — KURAL BUNU YASAKLIYOR (kaldırılamaz) ────────
 * `clips` kuralı posts'tan FARKLI ve bu fark kasıtlı:
 *
 *   allow get :  … || canReadPrivateContent(resource.data.authorId)   ← takipçi OK
 *   allow list:  … || resource.data.authorId == request.auth.uid      ← YALNIZ SAHİBİ
 *
 * Kuralın kendi yorumu gerekçeyi yazıyor: *"LIST (query): private kliplerin
 * enumerasyonu rules düzeyinde tamamen kapatılır … Follower'lara private
 * klipleri göstermek için Faz 2'de materialized feed (users/{uid}/
 * followingFeed/) eklenecek."*
 *
 * Yani gizli yazarın KLİBİ fallback kolda **hiçbir sorgu şekliyle**
 * getirilemez; onun tek meşru yolu materialized koldur (per-doc `get`).
 * Bu modül klipleri bilerek denemez — denese sorgunun TAMAMI reddedilirdi
 * ve akış "Feed yüklenemedi"ye düşerdi (K2 regresyonu, 2026-06-11).
 * 📌 Kalan fark bu tek eksende ve artık YAZILI: gönderiler iki kolda da
 *    aynı sözleşmede; klipler yalnız materialized kolda.
 *
 * ── FAIL-SAFE: EKLEMELİ, ASLA EKSİLTMELİ DEĞİL ──────────────────────────
 * Buradaki her sorgu mevcut akışın ÜSTÜNE ekleme yapar. Hepsi hata yutar
 * (`catch → []`): kural/index/kota ne dönerse dönsün **bugünkü akış aynen
 * kalır**, yalnız gizli-takip gönderileri eklenemez. Kapalı yönün bedeli
 * geri alınabilir ("bu tur eklenmedi"), açık yönün bedeli değil (akışın
 * tamamının reddi) — kanonun FAIL-OPEN/CLOSED ölçütü.
 *
 * ── INDEX: YENİ INDEX GEREKMİYOR (ölçüldü) ──────────────────────────────
 * · gizli yazar gönderileri → `authorId ASC, tarih DESC`
 *   = `firestore.indexes.json` **indexes[5]** (MEVCUT; `Feed.tsx`in
 *   `ownPostsQuery`si bugün aynı şekli kullanıyor).
 * · gizli yazar keşfi → `users` üzerinde `isPrivate ==` + `documentId() in`
 *   → iki eşitlik + `__name__`; otomatik tek-alan indeksiyle karşılanır,
 *   `fieldOverrides` içinde `users.isPrivate` muafiyeti YOK (ölçüldü).
 * ══════════════════════════════════════════════════════════════════════════
 */
import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { IN_OPERATOR_MAX } from "./clipsFeed";

/**
 * Keşif için taranacak EN FAZLA takip edilen yazar sayısı.
 *
 * Fallback kolun kendisi zaten `takipEdilenler.slice(0, IN_OPERATOR_MAX)`
 * ile 30 yazarda kesiyor (rank kolunda `HOME_FEED_RANK_MAX_CHUNKS` × 30).
 * Keşif tavanı 90 = 3 parça: taranmayan yazarın gönderisi eskisi gibi
 * gelmez, yani tavan hiçbir şeyi KAYBETTİRMEZ, yalnız kazancı sınırlar.
 */
export const GIZLI_YAZAR_TARAMA_TAVANI = 90;

/**
 * Gönderisi çekilecek EN FAZLA gizli yazar sayısı.
 * Her biri 1 sorgu → tavan aynı zamanda tur başına ek sorgu tavanıdır.
 */
export const GIZLI_YAZAR_SORGU_TAVANI = 12;

/**
 * Gizli yazar başına çekilecek gönderi sayısı.
 *
 * ⚠️ Bu sayı bir **kural bütçesi**, estetik bir sayfa boyutu değil:
 * gizli yazarın her dokümanı için kural `canReadPrivateContent` içinde
 * 1 `exists()` harcar. Firestore sorgu başına doküman-erişim sayısını
 * sınırlar; 6, o sınırın belirgin altında kalır. Yükseltme = sorgunun
 * TAMAMEN reddedilme riski (kısmi sonuç diye bir şey yok).
 */
export const GIZLI_YAZAR_POST_LIMITI = 6;

/** `users` dokümanının bu modülün okuduğu tek alanı. */
type KullaniciGizlilik = { isPrivate?: unknown };

/** Firestore'a hiç dokunmadan bölünebilen parça üretici (saf). */
export function parcalaraBol<T>(ogeler: readonly T[], parcaBoyu: number): T[][] {
  const parcalar: T[][] = [];
  if (parcaBoyu <= 0) return parcalar;
  for (let i = 0; i < ogeler.length; i += parcaBoyu) {
    parcalar.push(ogeler.slice(i, i + parcaBoyu));
  }
  return parcalar;
}

/**
 * Taranacak yazar kümesini kurar (SAF — test edilebilir).
 *
 * Viewer'ın KENDİSİ çıkarılır: kendi içeriği `Feed.tsx`te ayrı bir
 * `authorId == uid` sorgusuyla zaten geliyor (gizli hesapsa bile), buraya
 * girerse aynı gönderi iki kez çekilir.
 */
export function taranacakYazarlar(
  takipEdilenler: readonly string[] | null | undefined,
  viewerId: string | null | undefined,
  tavan: number = GIZLI_YAZAR_TARAMA_TAVANI,
): string[] {
  if (!Array.isArray(takipEdilenler)) return [];
  const gorulen = new Set<string>();
  const sonuc: string[] = [];
  for (const ham of takipEdilenler) {
    if (typeof ham !== "string") continue;
    const uid = ham.trim();
    if (!uid || uid === viewerId || gorulen.has(uid)) continue;
    gorulen.add(uid);
    sonuc.push(uid);
    if (sonuc.length >= tavan) break;
  }
  return sonuc;
}

/**
 * `users` dokümanlarından gizli olanların uid'lerini ayıklar (SAF).
 *
 * `isPrivate === true` KATI okunur: alan yoksa/`"true"` metniyse/`1` ise
 * yazar **public** sayılır. Sebep `firestore.rules profileIsPrivate` ile
 * aynı: eksik alan legacy public dokümandır ve public yol zaten çalışıyor
 * — burada yanlış pozitif, gereksiz bir sorgu (maliyet) demektir.
 */
export function gizliOlanlariAyikla(
  dokumanlar: readonly { id: string; data: () => KullaniciGizlilik | undefined }[],
): string[] {
  const gizli: string[] = [];
  for (const d of dokumanlar) {
    const veri = d.data();
    if (veri && veri.isPrivate === true) gizli.push(d.id);
  }
  return gizli;
}

/**
 * Takip edilenler arasından GİZLİ olanları bulur.
 *
 * Hata yutulur (`[]`): keşif başarısızsa gizli-takip gönderileri eklenmez,
 * mevcut akış aynen sürer.
 */
export async function gizliTakipEdilenleriBul(
  takipEdilenler: readonly string[] | null | undefined,
  viewerId: string | null | undefined,
): Promise<string[]> {
  const adaylar = taranacakYazarlar(takipEdilenler, viewerId);
  if (adaylar.length === 0) return [];

  const parcalar = parcalaraBol(adaylar, IN_OPERATOR_MAX);
  const sonuclar = await Promise.all(
    parcalar.map((ids) =>
      getDocs(
        query(
          collection(db, "users"),
          where(documentId(), "in", ids),
          where("isPrivate", "==", true),
        ),
      )
        .then((snap) => gizliOlanlariAyikla(snap.docs))
        .catch(() => [] as string[]),
    ),
  );

  return sonuclar.flat().slice(0, GIZLI_YAZAR_SORGU_TAVANI);
}

/**
 * Gizli yazarların son gönderilerini çeker (yazar başına TEK sorgu).
 *
 * Şekil `authorId == X` + `tarih desc` — `Feed.tsx ownPostsQuery` ile AYNI,
 * yani index #5 mevcut. `authorIsPrivate` filtresi BİLEREK yok: kuralın
 * `canReadPrivateContent` dalının çalışması için sorgunun o alanı
 * kısıtlamaması gerekir.
 *
 * Her sorgu ayrı ayrı hata yutar: bir yazar reddedilse (ör. takip edge'i
 * yeni silinmiş) diğerleri yine gelir.
 */
export async function gizliYazarGonderileriniGetir(
  gizliYazarlar: readonly string[] | null | undefined,
): Promise<Array<Record<string, unknown>>> {
  if (!Array.isArray(gizliYazarlar) || gizliYazarlar.length === 0) return [];
  const hedefler = gizliYazarlar.slice(0, GIZLI_YAZAR_SORGU_TAVANI);

  const sayfalar = await Promise.all(
    hedefler.map((authorId) =>
      getDocs(
        query(
          collection(db, "posts"),
          where("authorId", "==", authorId),
          orderBy("tarih", "desc"),
          limit(GIZLI_YAZAR_POST_LIMITI),
        ),
      )
        .then((snap) =>
          snap.docs.map((d) => ({
            ...(d.data() as Record<string, unknown>),
            id: d.id,
            type: "post" as const,
          })),
        )
        .catch(() => [] as Array<Record<string, unknown>>),
    ),
  );

  return sayfalar.flat();
}

/** Keşif + çekme — `Feed.tsx`in çağırdığı tek giriş noktası. */
export async function gizliTakipGonderileriniGetir(
  takipEdilenler: readonly string[] | null | undefined,
  viewerId: string | null | undefined,
): Promise<Array<Record<string, unknown>>> {
  try {
    const gizliYazarlar = await gizliTakipEdilenleriBul(takipEdilenler, viewerId);
    if (gizliYazarlar.length === 0) return [];
    return await gizliYazarGonderileriniGetir(gizliYazarlar);
  } catch {
    return [];
  }
}
